import { format } from 'date-fns';
import {
  Activity,
  Fragment,
  CSSProperties as ReactCSSProperties,
  useEffect,
  useState,
} from 'react';
import {
  Box,
  Button,
  Center,
  Checkbox,
  Column,
  ConfirmPopup,
  CustomSpinner,
  Divider,
  EmptyScreen,
  Row,
  ScrollArea,
  Text,
  ZStack,
} from '~/commonComponents';
import { IEligibleCandidateEntity, useGetEligibleCandidates } from '~/domain/PermanentJobs';
import { UserProfession, UserSubProfession } from '~/enums/UserProfession';
import { IToggleControls } from '~/hooks/useToggle';
import { CalendarTodayFilterIcon } from '~/images/emptyScreenIcons';
import { Colors } from '~/themes/colors';

interface IInvitePopupProps {
  jobPostingId: string;
  controls?: IToggleControls;
  isOpen?: boolean;
  value?: string[];
  onConfirm?: (professionals: IEligibleCandidateEntity[]) => void;
}

export const InvitePopup = ({
  controls,
  isOpen,
  jobPostingId,
  value = [],
  onConfirm,
}: IInvitePopupProps) => {
  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(value.map((id) => [id, true])),
  );

  useEffect(() => {
    if (isOpen) {
      setSelectedIds(Object.fromEntries(value.map((id) => [id, true])));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const { data, isLoading } = useGetEligibleCandidates(
    { postingId: jobPostingId },
    { enabled: isOpen && !!jobPostingId },
  );

  const handleToggle = (id: string) => {
    setSelectedIds((prev) => {
      if (prev[id]) {
        const newVal = { ...prev };
        delete newVal[id];
        return newVal;
      }
      return { ...prev, [id]: true };
    });
  };

  const totalData = data.length;
  const isEmpty = !isLoading && totalData === 0;
  const hasSelectedItem = Object.entries(selectedIds).length > 0;

  if (!isOpen) {
    return null;
  }

  return (
    // @ts-expect-error - Confirm Popup still written in Javascript
    <ConfirmPopup
      modalStyle={{ width: 568, height: 689 } as ReactCSSProperties}
      showClosePopupIcon
      title="Invite Candidates"
      closePopup={controls?.setOff}
    >
      {isLoading ? (
        <CustomSpinner isLoading size={20} />
      ) : (
        <>
          <Activity mode={isEmpty ? 'visible' : 'hidden'}>
            <EmptyScreen
              Icon={CalendarTodayFilterIcon}
              title="No candidates available at this time!"
              description="It looks like you don’t have any candidates for this job yet! Once a professional applies, they will appear on here where you will be able send direct invites to upcoming interviews."
              descriptionStyle={{
                textAlign: 'center',
              }}
            />
          </Activity>
          <Activity mode={isEmpty ? 'hidden' : 'visible'}>
            <ScrollArea style={{ flex: 1 }}>
              <Column paddingInline={40}>
                <Text color={Colors.neutral_500} italic>
                  Send invites to all candidates who’ve applied to this job. They’ll be confirmed
                  once they accept at the listed rate and time.
                </Text>
                <Box height={32} />
                {data.map((it) => {
                  return (
                    <Fragment key={it.id}>
                      <Row
                        role="button"
                        cursor="pointer"
                        gap={20}
                        onClick={() => handleToggle(it.id)}
                      >
                        <Center position="relative" width={58} height={58} borderRadius="50%">
                          <img
                            src={it.profileImage}
                            style={{
                              borderRadius: '50%',
                              height: '100%',
                              width: '100%',
                              objectFit: 'cover',
                              display: 'block',
                            }}
                            alt="profile_image"
                          />
                          <ZStack
                            background={Colors.white}
                            borderColor={Colors.white}
                            borderRadius={5}
                            borderStyle="solid"
                            borderWidth={2}
                            lineHeight={0}
                            right={-8}
                            top={0}
                          >
                            <Checkbox
                              checked={!!selectedIds[it.id]}
                              style={{ height: 18, width: 18 }}
                            />
                          </ZStack>
                        </Center>
                        <Column flex={1} gap={2}>
                          <Text bold fontSize={18}>
                            {it.fullName}
                          </Text>
                          <Row gap={4}>
                            <Text color={Colors.neutral_500}>
                              {UserSubProfession.getName(it.subProfession?.[0] || '') ??
                                UserProfession.getName(it.profession?.[0])}
                            </Text>
                            <Text color={Colors.neutral_500}>&bull;</Text>
                            <Text color={Colors.neutral_500}>
                              {it.yearsOfExperience} yrs Experience
                            </Text>
                          </Row>
                          <Text color={Colors.neutral_500}>
                            Applied on: {format(new Date(it.appliedAt), 'EEE, MMM d, yyyy')}
                          </Text>
                        </Column>
                      </Row>
                      <Divider height={2} />
                    </Fragment>
                  );
                })}
              </Column>
            </ScrollArea>
            <Button
              disabled={!hasSelectedItem}
              text="Confirm"
              style={{ width: 296, height: 48, marginTop: 60 }}
              onClick={() => onConfirm?.(data.filter((c) => selectedIds[c.id]))}
            />
          </Activity>
        </>
      )}
    </ConfirmPopup>
  );
};
